import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * ErrorBoundary — Cattura errori React non gestiti e mostra UI di recovery.
 *
 * Uso:
 *   <ErrorBoundary>           → boundary root
 *   <ErrorBoundary area="App"> → boundary di area (mostra il nome nell'UI)
 *
 * Con Sentry: sostituire con Sentry.ErrorBoundary quando il DSN è configurato.
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        // Sentry: Sentry.captureException(error, { extra: errorInfo });
        console.error('[ErrorBoundary]', this.props.area || 'Root', error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const { area = 'questa pagina' } = this.props;
        const msg = this.state.error?.message || 'Errore sconosciuto';

        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#030014',
                padding: 24,
            }}>
                <div style={{
                    maxWidth: 520,
                    width: '100%',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 16,
                    padding: '40px 36px',
                    textAlign: 'center',
                    backdropFilter: 'blur(12px)',
                }}>
                    {/* Icona */}
                    <div style={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: 'rgba(239,68,68,0.12)',
                        border: '1px solid rgba(239,68,68,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 24px',
                    }}>
                        <AlertTriangle size={28} color="#ef4444" />
                    </div>

                    <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>
                        Qualcosa è andato storto
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: '0 0 8px' }}>
                        Si è verificato un errore inatteso in <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{area}</strong>.
                    </p>
                    <p style={{
                        color: 'rgba(239,68,68,0.7)',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        background: 'rgba(239,68,68,0.06)',
                        border: '1px solid rgba(239,68,68,0.15)',
                        borderRadius: 8,
                        padding: '10px 14px',
                        margin: '16px 0 28px',
                        textAlign: 'left',
                        wordBreak: 'break-all',
                        maxHeight: 80,
                        overflow: 'hidden',
                    }}>
                        {msg}
                    </p>

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                        <button
                            onClick={this.handleReset}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '10px 20px', borderRadius: 10,
                                background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                                border: 'none', color: '#fff',
                                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            <RefreshCw size={15} /> Riprova
                        </button>
                        <button
                            onClick={() => { this.handleReset(); window.location.href = '/'; }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '10px 20px', borderRadius: 10,
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: 14, fontWeight: 500, cursor: 'pointer',
                            }}
                        >
                            <Home size={15} /> Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
